import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Fuel, 
  Truck, 
  Warehouse, 
  Shield, 
  CheckCircle2, 
  ArrowRight,
  TrendingUp,
  Users,
  Globe,
  Award
} from 'lucide-react';
import { 
  heroContent, 
  services, 
  industries, 
  whyChooseUs, 
  companyInfo,
  targetMarkets,
  sisterCompanies
} from '../mock';

export const Home = () => {
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);

  const stats = [
    { label: 'Years in Operation', value: '7+', icon: TrendingUp },
    { label: 'Industries Served', value: '10+', icon: Users },
    { label: 'Countries Reached', value: '7+', icon: Globe },
    { label: 'B-BBEE Level', value: '1', icon: Award }
  ];

  const iconMap = {
    Fuel: Fuel,
    Truck: Truck,
    Warehouse: Warehouse,
    Shield: Shield
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/hero-fuel-truck.png" 
            alt="Fuel Truck" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/85 via-emerald-800/75 to-teal-900/60"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <Badge className="mb-4 bg-white text-emerald-700 hover:bg-emerald-50">
                Level 1 B-BBEE Certified
              </Badge>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                {heroContent.title}
              </h1>
              <p className="text-xl text-emerald-50 mb-8">
                {heroContent.subtitle}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/quote">
                  <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50 transition-all duration-300 hover:shadow-lg hover:scale-105">
                    {heroContent.cta} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-emerald-700 transition-all duration-300 bg-transparent">
                    {heroContent.secondaryCta}
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <Card key={index} className="bg-white/95 backdrop-blur-sm border-white/30 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="pt-6">
                      <Icon className="h-8 w-8 text-emerald-600 mb-3" />
                      <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                      <div className="text-sm text-gray-600">{stat.label}</div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-200 text-base px-4 py-1">Our Services</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive Fuel Solutions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From wholesale supply to storage consulting, we provide end-to-end petroleum solutions
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 6).map((service) => {
              const Icon = iconMap[service.icon] || Fuel;
              return (
                <Card key={service.id} className="border-gray-200 hover:border-emerald-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                  <CardHeader>
                    <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-4 group-hover:bg-emerald-600 transition-colors duration-300">
                      <Icon className="h-6 w-6 text-emerald-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-gray-600">{service.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-all duration-300">
                View All Services <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-200 text-base px-4 py-1">Industries</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Powering Key Industries</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Trusted fuel partner for logistics, mining, and agriculture across Southern Africa
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {industries.slice(0, 3).map((industry) => (
              <Card key={industry.id} className="overflow-hidden bg-white border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={industry.image} 
                    alt={industry.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                <CardHeader>
                  <CardTitle className="text-2xl">{industry.name}</CardTitle>
                  <CardDescription className="text-gray-600 text-base">
                    {industry.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {industry.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/industries">
              <Button size="lg" variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-all duration-300">
                View All {industries.length}+ Industries <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 text-base px-4 py-1">Why Choose Us</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Your Trusted Petroleum Partner</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Quality, reliability, and service excellence that sets us apart
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="flex gap-4 p-6 rounded-lg border border-gray-200 hover:border-emerald-300 hover:shadow-lg transition-all duration-300 bg-white">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Storage Infrastructure Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 text-base px-4 py-1">Storage Infrastructure</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">World-Class Diesel Storage Facilities</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              State-of-the-art storage tanks ensuring product quality, safety, and reliable supply
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            <div className="relative h-64 rounded-2xl overflow-hidden group">
              <img src="/images/diesel-tanks-1.png" alt="Rented Storage Facilities" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                <div className="text-white">
                  <h3 className="text-xl font-bold mb-1">Rented Storage Facilities</h3>
                  <p className="text-sm text-emerald-100">Strategic tank farm rentals</p>
                </div>
              </div>
            </div>

            <div className="relative h-64 rounded-2xl overflow-hidden group">
              <img src="/images/diesel-tanks-2.png" alt="Diesel Storage Tank Close-up" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                <div className="text-white">
                  <h3 className="text-xl font-bold mb-1">Quality-Controlled</h3>
                  <p className="text-sm text-emerald-100">Monitored 24/7</p>
                </div>
              </div>
            </div>

            <div className="relative h-64 rounded-2xl overflow-hidden group">
              <img src="/images/diesel-tanks-3.png" alt="Leased Bulk Storage" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                <div className="text-white">
                  <h3 className="text-xl font-bold mb-1">Leased Bulk Storage</h3>
                  <p className="text-sm text-emerald-100">Diesel, Petrol, Jet Fuel</p>
                </div>
              </div>
            </div>

            <div className="relative h-64 rounded-2xl overflow-hidden group">
              <img src="/images/mine-storage.png" alt="Mine Storage Facility" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                <div className="text-white">
                  <h3 className="text-xl font-bold mb-1">End User Storage — Mining</h3>
                  <p className="text-sm text-emerald-100">On-site mine storage installations</p>
                </div>
              </div>
            </div>

            <div className="relative h-64 rounded-2xl overflow-hidden group">
              <img src="/images/logistics-centre-storage.png" alt="Logistics Centre Storage" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                <div className="text-white">
                  <h3 className="text-xl font-bold mb-1">End User Storage — Logistics</h3>
                  <p className="text-sm text-emerald-100">Depot tanks serving tipper truck fleets</p>
                </div>
              </div>
            </div>

            <div className="relative h-64 rounded-2xl overflow-hidden group">
              <img src="/images/bus-depot-storage.png" alt="Bus Depot Fuel Storage" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                <div className="text-white">
                  <h3 className="text-xl font-bold mb-1">End User Storage Facilities</h3>
                  <p className="text-sm text-emerald-100">Fuel depot at bus parking depot</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            {[
              { label: 'Storage Capacity', value: 'Bulk Volumes' },
              { label: 'Compliance', value: 'Fully Certified' },
              { label: 'Safety Standards', value: 'ISO Certified' },
              { label: 'Quality Testing', value: 'Lab Verified' }
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200 text-center">
                <div className="text-emerald-600 font-bold text-lg">{item.value}</div>
                <div className="text-sm text-gray-600">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regional and International Subsidiaries */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 text-base px-4 py-1">Global Network</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Regional and International Subsidiaries</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our network of regional and international subsidiaries plus import partners across {sisterCompanies.length} countries enables seamless cross-jurisdictional trade and efficient regional supply chains
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {sisterCompanies.map((company, index) => (
              <Card key={index} className="border-2 border-gray-200 hover:border-emerald-400 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                <CardHeader>
                  <div className="flex items-center gap-4 mb-3">
                    <div className="text-5xl">{company.flag}</div>
                    <div>
                      <CardTitle className="text-xl">{company.country}</CardTitle>
                      <Badge variant="secondary" className="mt-1 bg-emerald-50 text-emerald-700">
                        {company.role}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{company.description}</p>
                </CardHeader>
              </Card>
            ))}
          </div>

          <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200">
            <CardContent className="pt-6">
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-emerald-600 mb-1">5+</div>
                  <div className="text-sm text-gray-700 font-medium">Countries Network</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-emerald-600 mb-1">Seamless</div>
                  <div className="text-sm text-gray-700 font-medium">Cross-Border Trade</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-emerald-600 mb-1">Global</div>
                  <div className="text-sm text-gray-700 font-medium">Supply Access via UAE</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Target Markets */}
      <section className="py-20 px-6 bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Serving Southern Africa & Beyond</h2>
          <p className="text-xl mb-12 text-emerald-50">
            Operating across {targetMarkets.length} countries with regional and international subsidiaries enabling easy trade facilitation
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {targetMarkets.map((market, index) => (
              <Badge key={index} variant="secondary" className="px-4 py-2 text-base bg-white/20 hover:bg-white/30 text-white border-white/30">
                {market.country} • {market.status}
              </Badge>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-emerald-50 transition-all duration-300 hover:shadow-xl hover:scale-105">
                Partner With Us <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;